/**
 * NECS service packages — Collections lineup
 *
 * Updated Feb 2026 per CEO directive (PhD):
 *   • Every package now carries its OWN fully-listed services array
 *     (no more "Everything in X Collection" placeholders). Full
 *     transparency so any dashboard — Tenant, PM, Site Staff, Admin —
 *     can just spread `services` and be done.
 *   • Renamed all six tiers to "Collection" branding
 *   • Refreshed bundled services per the new investment ladder
 *   • Added Professional Interior & Exterior Window Cleaning to
 *     the top two collections only (Executive · biannual,
 *     Presidential · quarterly), in the correct order.
 *
 * NOTE: prices live here for the public website & internal dashboards.
 *       The AI Noble assistant has been instructed to NEVER disclose them
 *       publicly — see /app/backend/routes/support_chat.py SYSTEM_PROMPT.
 */

// Base services from Essential — reused as the foundation of every tier.
const ESSENTIAL_SERVICES = [
  "Single Valet Trash & Recycling Support (Monday to Friday)",
  "QR-coded trash containers",
  "NECS Resident App",
  "Operational reporting",
  "Common-area trash support",
  "24/7 customer service",
];

const PROFESSIONAL_ADDITIONS = [
  "Enhanced operational monitoring",
  "Dumpster treatment (bi-annual)",
  "Expanded management reporting",
];

const ELITE_ADDITIONS = [
  "Property Appearance Patrol",
  "Expanded cleanliness services",
  "Enhanced community presentation",
];

const SIGNATURE_ADDITIONS = [
  "Annual pressure washing",
  "Monthly bulk removal support",
  "Overflow dumpster management",
  "Priority support",
  "Additional exterior property maintenance",
];

const EXECUTIVE_ADDITIONS = [
  "Double Valet Trash & Recycling Support (Sunday to Friday)",
  "Sunday–Friday service",
  "Quarterly dumpster treatment",
  "Bi-weekly bulk removal",
  "Property Appearance Patrol",
  "Premium priority support",
  "Professional interior & exterior window cleaning — twice annually (biannual) for leasing offices and designated common areas",
];

const PRESIDENTIAL_ADDITIONS = [
  "Weekly eco-friendly amenity cleaning",
  "Clubhouse and fitness center cleaning",
  "Leasing office sanitization",
  "VIP customer support",
  "Expanded luxury property services",
  "Sustainability consulting support",
  "Professional interior & exterior window cleaning — every quarter (four times annually) for leasing offices and designated common areas",
];

/*
 * NOTE (Feb 2026 — Price Protection directive):
 * All prices removed from this file. NECS pricing is BESPOKE and quote-only.
 * Every UI that previously displayed `.price` now falls back to the string
 * "Contact us for a custom quote" via the getter at the bottom of this file.
 * Do NOT reintroduce hardcoded dollar figures — the /api/knowledge endpoint
 * also blocks `$` symbols server-side, and the AI Receptionist is prompted
 * to redirect all pricing questions to "Request a Quote" or "Try the Live
 * Demo".
 */
export const QUOTE_PLACEHOLDER = "Contact us for a custom quote";

export const packageDetails = {
  "Essential Collection": {
    price: QUOTE_PLACEHOLDER,
    designedFor:
      "Conventional apartment communities seeking reliable valet trash, recycling support, and operational reporting.",
    services: [...ESSENTIAL_SERVICES],
    shift: "Single Shift · Monday–Friday at 8 PM",
  },
  "Professional Collection": {
    price: QUOTE_PLACEHOLDER,
    designedFor:
      "Communities that want everything in Essential plus enhanced monitoring and bi-annual dumpster treatment.",
    services: [...ESSENTIAL_SERVICES, ...PROFESSIONAL_ADDITIONS],
    shift: "Single Shift · Monday–Friday at 8 PM",
  },
  "Elite Collection": {
    price: QUOTE_PLACEHOLDER,
    designedFor:
      "Properties that need Property Appearance Patrol coverage, expanded cleanliness services, and priority response.",
    services: [
      ...ESSENTIAL_SERVICES,
      ...PROFESSIONAL_ADDITIONS,
      ...ELITE_ADDITIONS,
    ],
    shift: "Single Shift · Monday–Friday at 8 PM",
  },
  "Signature Collection": {
    price: QUOTE_PLACEHOLDER,
    designedFor:
      "Communities that benefit from annual pressure washing, monthly bulk removal, and exterior maintenance.",
    services: [
      ...ESSENTIAL_SERVICES,
      ...PROFESSIONAL_ADDITIONS,
      ...ELITE_ADDITIONS,
      ...SIGNATURE_ADDITIONS,
    ],
    shift: "Single Shift · Monday–Friday at 8 PM",
  },
  "Noble Empire Executive Collection": {
    price: QUOTE_PLACEHOLDER,
    designedFor:
      "Premium communities needing double-shift coverage, biannual window cleaning, and enhanced reporting.",
    services: [
      // Executive replaces single shift with double shift up-front, then
      // everything else stacks. We drop the plain "Single Valet Trash…"
      // entry since it's superseded by the double-shift version.
      ...ESSENTIAL_SERVICES.filter((s) => !s.startsWith("Single Valet Trash")),
      ...PROFESSIONAL_ADDITIONS,
      ...ELITE_ADDITIONS,
      ...SIGNATURE_ADDITIONS.filter((s) => s !== "Annual pressure washing"), // annual pressure washing is elevated below
      ...EXECUTIVE_ADDITIONS,
      "Annual pressure washing",
    ],
    shift: "Double Shift · Sunday–Friday",
  },
  "Noble Empire Presidential Collection": {
    price: QUOTE_PLACEHOLDER,
    designedFor:
      "Flagship luxury and mixed-use properties demanding VIP-grade service and sustainability support.",
    services: [
      ...ESSENTIAL_SERVICES.filter((s) => !s.startsWith("Single Valet Trash")),
      ...PROFESSIONAL_ADDITIONS,
      ...ELITE_ADDITIONS,
      ...SIGNATURE_ADDITIONS.filter((s) => s !== "Annual pressure washing"),
      ...EXECUTIVE_ADDITIONS,
      "Annual pressure washing",
      ...PRESIDENTIAL_ADDITIONS,
    ],
    shift: "Noble Empire Premium Service · 6-day program",
  },
};

// ─── Back-compat shims so older code references still resolve ────────
// Older components & inquiry rows may still reference the legacy package
// names. Map them to the new Collection identities so nothing breaks
// while the rest of the site rolls forward.
const LEGACY_ALIASES = {
  "Standard Package": "Essential Collection",
  "Premium Package": "Professional Collection",
  "Platinum Package": "Elite Collection",
  "Magnum Package": "Signature Collection",
  "Noble Package": "Noble Empire Executive Collection",
  "Deluxe Empire Package": "Noble Empire Presidential Collection",
};

/**
 * Walks the "Everything in X Collection" inheritance chain and returns a
 * fully flattened, deduplicated list of services for the given package.
 * Used by the Tenant dashboard so residents see every single service
 * they're actually entitled to — no "Everything in Essential" mystery
 * placeholders. The flattening order is base-tier services first, then
 * the tier-specific additions, mirroring the upsell ladder.
 */
const INHERITED_PREFIX = /^Everything in (the )?/i;

export const getFlattenedServices = (packageName) => {
  const visited = new Set();
  const out = [];
  const seen = new Set();   // lowercase service text for dedupe

  const walk = (name) => {
    if (!name || visited.has(name)) return;
    visited.add(name);
    // Smart tier-name lookup: try the literal name first; if that fails,
    // try the legacy alias map (e.g. "Premium Package" → "Professional
    // Collection"); finally, try prefixing with "Noble Empire " for the
    // luxury tiers (since the source data says "Everything in the
    // Executive Collection" but the actual key is "Noble Empire
    // Executive Collection").
    let tier =
      packageDetails[name] ||
      packageDetails[LEGACY_ALIASES[name]] ||
      packageDetails[`Noble Empire ${name}`];
    if (!tier) return;
    for (const svc of tier.services || []) {
      if (typeof svc !== "string") continue;
      const stripped = svc.trim();
      if (INHERITED_PREFIX.test(stripped)) {
        // Resolve the inherited parent tier name.
        const parent = stripped.replace(INHERITED_PREFIX, "").trim();
        walk(parent);
        continue;
      }
      const key = stripped.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        out.push(stripped);
      }
    }
  };

  walk(packageName || "Essential Collection");
  if (out.length === 0) walk("Essential Collection");
  return out;
};

export const getPackageDetails = (packageName) => {
  if (!packageName) return packageDetails["Essential Collection"];
  if (packageDetails[packageName]) return packageDetails[packageName];
  const alias = LEGACY_ALIASES[packageName];
  if (alias) return packageDetails[alias];
  return packageDetails["Essential Collection"];
};

/**
 * Iter-130 · Section 1 — resolve any stored `package_type` (including
 * legacy aliases like "Premium Package") to the current canonical
 * Collection name. Used by Tenant + PM dashboards so a renamed package
 * always displays its up-to-date label.
 */
export const getCanonicalPackageName = (packageName) => {
  if (!packageName) return "Essential Collection";
  if (packageDetails[packageName]) return packageName;
  if (LEGACY_ALIASES[packageName]) return LEGACY_ALIASES[packageName];
  const withPrefix = `Noble Empire ${packageName}`;
  if (packageDetails[withPrefix]) return withPrefix;
  return "Essential Collection";
};
